import { useEffect } from 'react'
import { OG_CARD } from '@/lib/card'
import { headFor } from '@/lib/seo'
import { IS_PRODUCTION, SITE_URL } from '@/lib/site'
import { useI18n } from '@/i18n/I18nProvider'
import { localeDef } from '@/i18n/locales'
import type { RouteDef } from './routes'

/**
 * Keeps the live document's head in step after a client-side navigation.
 *
 * The *first* paint's head comes from the prerendered HTML — this component
 * does not produce it, and must not fight it. Both read `headFor()`, so what
 * it writes on hydration is byte-identical to what is already there.
 *
 * Every tag it manages carries `data-seo`, and it clears that set before
 * writing. Without the marker the choice is between leaving stale tags behind
 * (two canonicals, one wrong) and clearing tags somebody else owns.
 *
 * **It passes `OG_CARD` because the sentence above has to stay true.** The
 * prerender passes the card only when it has found the file on disk; this side
 * cannot look at a disk, so it passes the constant. Get that wrong and hydration
 * DELETES the card tags the prerender wrote — which no scraper would notice,
 * because none of them run JavaScript, and which would therefore only ever show
 * up as a person opening devtools, seeing no `og:image`, and concluding the card
 * is broken. That is the failure this file's whole comment is about: a diagnostic
 * that disagrees with what actually shipped.
 *
 * The reverse — the file is missing, the prerender emits nothing, and the client
 * adds a tag naming a 404 — costs nothing, because the tag only ever exists in a
 * browser that is not fetching it. `pnpm verify` is what reports the absence.
 */
export default function Seo({ route }: { route: RouteDef }) {
  const { locale } = useI18n()

  useEffect(() => {
    const head = headFor({
      route,
      locale,
      origin: SITE_URL,
      indexable: IS_PRODUCTION,
      card: OG_CARD,
    })

    document.title = head.title

    const def = localeDef(locale)
    document.documentElement.lang = def.code
    document.documentElement.dir = def.dir

    for (const el of Array.from(document.head.querySelectorAll('[data-seo]'))) {
      el.remove()
    }
    for (const { tag, attrs } of head.tags) {
      const el = document.createElement(tag)
      for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v)
      el.setAttribute('data-seo', '')
      document.head.appendChild(el)
    }
  }, [route, locale])

  return null
}
